import React, { useState } from "react";
import { connect } from "react-redux";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CircularProgress from "@mui/material/CircularProgress";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const mapStateToProps = (state) => ({
  map: state.MapReducer,
});

function getLeaflet(mapState) {
  return mapState?.leafletMap || mapState?.map || window?.__leafletMap || null;
}

function getPdfSizeMM(format, orientation) {
  const sizes = {
    A4: { portrait: [210, 297], landscape: [297, 210] },
    A3: { portrait: [297, 420], landscape: [420, 297] },
    A2: { portrait: [420, 594], landscape: [594, 420] },
  };
  return sizes?.[format]?.[orientation] || sizes.A4.landscape;
}

function hexFromCssColor(css) {
  if (!css) return "#cccccc";
  if (css.startsWith("#")) return css;

  const m = css.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return "#cccccc";

  const r = Number(m[1]).toString(16).padStart(2, "0");
  const g = Number(m[2]).toString(16).padStart(2, "0");
  const b = Number(m[3]).toString(16).padStart(2, "0");

  return `#${r}${g}${b}`;
}

function isColorSwatch(el) {
  if (!el) return false;
  const st = window.getComputedStyle(el);
  const bg = st.backgroundColor;
  const w = parseFloat(st.width || "0");
  const h = parseFloat(st.height || "0");

  return (
    bg &&
    bg !== "transparent" &&
    bg !== "rgba(0, 0, 0, 0)" &&
    w >= 8 &&
    h >= 8 &&
    w <= 30 &&
    h <= 30
  );
}

function findClosestLegendRow(node) {
  let cur = node;
  while (cur) {
    const txt = (cur.textContent || "").replace(/\s+/g, " ").trim();
    if (txt) return cur;
    cur = cur.parentElement;
  }
  return null;
}

function cleanLegendTitle(raw) {
  let s = String(raw || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  s = s.replace(/^[①②]\s*/u, "");
  s = s.replace(/^[^(\dA-Za-zÁÉÍÓÖŐÚÜŰáéíóöőúüű-]{1,4}\s*(?=\(\d+\))/u, "");
  s = s.replace(/^[A-Za-z$]{1,3}\s*(?=\(\d+\))/u, "");
  s = s.replace(/^[|~`_^;:.,-]+\s*/u, "");

  s = s.replace(
    /^(\(\d+\)\s+)?((?:[A-Za-zÁÉÍÓÖŐÚÜŰáéíóöőúüű]\s+){3,}[A-Za-zÁÉÍÓÖŐÚÜŰáéíóöőúüű])\b/u,
    (_, p1 = "", p2 = "") => {
      const compact = p2.replace(/\s+/g, "");
      return `${p1}${compact}`;
    }
  );

  return s.replace(/\s+/g, " ").trim();
}

function cleanLegendLabel(raw) {
  return String(raw || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getGridExtraLinesForPdf(title = "", map = null) {
  const t = String(title || "").toLowerCase().trim();

  const exactGridLegendTitles = [
    "composite development levels",
    "changes in development levels",
  ];

  const isGridLegend = exactGridLegendTitles.some((s) => t.includes(s));

  if (!isGridLegend) return [];

  return [
    { text: "(warmer colour = more developed)", type: "caption" },
    { text: "Decile", type: "subtitle" },
  ];
}

function collectLegendSections(map) {
  const legendEl = document.querySelector(".legend-panel");
  if (!legendEl) return [];

  const sections = [];

  const candidates = Array.from(
    legendEl.querySelectorAll(
      ".MuiTypography-subtitle2, .MuiTypography-subtitle1, h2, h3, h4, h5, h6, strong"
    )
  );

  const sectionTitleEls = candidates.filter((el) => {
    const txt = cleanLegendTitle(el.textContent || "");
    if (!txt) return false;

    if (txt.toLowerCase() === "legend") return false;
    if (txt.length < 3) return false;

    if (/\(\d+\)/.test(txt)) return true;

    let node = el.nextElementSibling;
    let swatchCount = 0;
    let steps = 0;

    while (node && steps < 6) {
      const swatches = Array.from(
        node.querySelectorAll ? node.querySelectorAll("*") : []
      ).filter((child) => isColorSwatch(child));

      if (isColorSwatch(node)) {
        swatchCount += 1;
      }

      swatchCount += swatches.length;

      if (swatchCount > 0) return true;

      node = node.nextElementSibling;
      steps += 1;
    }

    return false;
  });

  if (!sectionTitleEls.length) {
    const rows = [];
    const seen = new Set();

    const swatches = Array.from(legendEl.querySelectorAll("*")).filter((el) =>
      isColorSwatch(el)
    );

    swatches.forEach((sw) => {
      const rowNode = findClosestLegendRow(sw.parentElement || sw);
      if (!rowNode) return;

      let label = cleanLegendLabel(rowNode.textContent || "");
      if (!label) return;
      if (label.toLowerCase() === "legend") return;
      if (label.length > 60) return;

      const color = hexFromCssColor(window.getComputedStyle(sw).backgroundColor);
      const key = `${color}|${label}`;
      if (seen.has(key)) return;
      seen.add(key);

      rows.push({ label, color });
    });

    if (rows.length) {
      sections.push({
        title: "",
        extraLines: getGridExtraLinesForPdf("", map),
        rows,
      });
    }

    return sections;
  }

  sectionTitleEls.forEach((titleEl, index) => {
    const title = cleanLegendTitle(titleEl.textContent || "");
    if (!title) return;

    const rows = [];
    const seen = new Set();

    let node = titleEl.nextElementSibling;
    const stopEl = sectionTitleEls[index + 1] || null;

    while (node && node !== stopEl) {
      const swatches = Array.from(
        node.querySelectorAll ? node.querySelectorAll("*") : []
      ).filter((el) => isColorSwatch(el));

      if (isColorSwatch(node)) {
        swatches.unshift(node);
      }

      swatches.forEach((sw) => {
        const rowNode = findClosestLegendRow(sw.parentElement || sw);
        if (!rowNode) return;

        let label = cleanLegendLabel(rowNode.textContent || "");
        if (!label) return;
        if (label === title) return;
        if (label.toLowerCase() === "legend") return;
        if (label.length > 60) return;

        const color = hexFromCssColor(window.getComputedStyle(sw).backgroundColor);
        const key = `${color}|${label}`;
        if (seen.has(key)) return;
        seen.add(key);

        rows.push({ label, color });
      });

      node = node.nextElementSibling;
    }

    if (rows.length) {
      sections.push({
        title,
        extraLines: getGridExtraLinesForPdf(title, map),
        rows,
      });
    }
  });

  return sections;
}

function wrapPdfTextGreedy(pdf, text, maxWidth) {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    if (!clean) return [];
  
    const words = clean.split(" ");
    const lines = [];
    let current = "";
  
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      const w = pdf.getTextWidth(test);
  
      if (w <= maxWidth || !current) {
        current = test;
      } else {
        lines.push(current);
        current = word;
      }
    }
  
    if (current) lines.push(current);
    return lines;
  }
  
  function drawLegendSectionsToPdf(pdf, opts = {}) {
    const {
      map = null,
      mapX = 0,
      mapY = 0,
      mapW = 100,
      mapH = 100,
  
      maxWidth = 42,
      boxSize = 3,
      titleFontSize = 8,
      labelFontSize = 7,
    } = opts;
  
    const sections = collectLegendSections(map);
    if (!sections.length) return;
  
    // Egységes jobb és alsó margó — innen indul a legenda.
    const edgeMargin = 1.6;
  
    // Bal belső margók
    const titleLeftPad = 2;
    const extraLeftPad = 2;
    const labelLeftPad = 8;
  
    // A cím szinte a teljes panel szélességet használhatja.
    // Itt direkt nagyobb hasznos szélességet adunk, mint korábban.
    const titleTextWidth = maxWidth - 1.2;
    const labelTextWidth = maxWidth - 11;
  
    const titleLineH = 3.2;
    const labelLineH = 3.0;
  
    const topPad = 4.2;
    const bottomPad = 1.2;
  
    const gapTitleToLine = -1.0;
    const gapLineToItems = 2.0;
    const rowGap = 0.7;
    const sectionGap = 7;
  
    let cursorY = topPad;
    let contentBottom = topPad;
    const layoutSections = [];
  
    sections.forEach((section, sectionIdx) => {
      const layoutSection = {
        title: null,
        titleLines: [],
        titleY: null,
        extraLines: Array.isArray(section.extraLines) ? section.extraLines : [],
        extraLineItems: [],
        lineY: null,
        rows: [],
      };
  
      if (section.title) {
        const cleanTitle = cleanLegendTitle(section.title);
  
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(titleFontSize);
        const titleLines = wrapPdfTextGreedy(pdf, cleanTitle, titleTextWidth);
  
        layoutSection.title = cleanTitle;
        layoutSection.titleLines = titleLines;
        layoutSection.titleY = cursorY;
  
        let localCursorY = cursorY + titleLines.length * titleLineH;
  
        layoutSection.extraLines.forEach((line) => {
          const item = {
            text: String(line.text || ""),
            type: line.type || "caption",
            y: localCursorY + 2.0,
          };
  
          localCursorY += line.type === "caption" ? 3.0 : 4.0;
          layoutSection.extraLineItems.push(item);
        });
  
        const lineY = localCursorY + gapTitleToLine - 0.3;
        layoutSection.lineY = lineY;
  
        cursorY = lineY + gapLineToItems;
        contentBottom = Math.max(contentBottom, lineY);
      }
  
      section.rows.forEach((row, rowIdx) => {
        const cleanLabel = cleanLegendLabel(row.label);
  
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(labelFontSize);
        const labelLines = wrapPdfTextGreedy(pdf, cleanLabel, labelTextWidth);
  
        const rowHeight = Math.max(boxSize, labelLines.length * labelLineH);
  
        const rowTopY = cursorY;
        const squareY = rowTopY + rowHeight / 2 - boxSize / 2;
        const rowBottomY = Math.max(rowTopY + rowHeight, squareY + boxSize);
  
        layoutSection.rows.push({
          color: row.color,
          labelLines,
          rowTopY,
          rowHeight,
          squareY,
          rowBottomY,
        });
  
        contentBottom = Math.max(contentBottom, rowBottomY);
  
        cursorY += rowHeight;
  
        const isLastRowInSection = rowIdx === section.rows.length - 1;
        if (!isLastRowInSection) {
          cursorY += rowGap;
        }
      });
  
      layoutSections.push(layoutSection);
  
      const isLastSection = sectionIdx === sections.length - 1;
      if (!isLastSection) {
        cursorY += sectionGap;
        contentBottom = Math.max(contentBottom, cursorY);
      }
    });
  
    const totalHeight = contentBottom + bottomPad;
  
    // Mindig lentről induljon.
    let x = mapX + mapW - maxWidth - edgeMargin;
    let y = mapY + mapH - totalHeight - edgeMargin;
  
    // Ha túl magas és felül kilógna, akkor SEM felülről pozicionáljuk újra,
    // hanem megtartjuk az alsó igazítást.
    // A zoom gombok eltakarásához csak a jobb oldali margót vesszük kicsire.
    // Így az alsó és jobb margó azonos marad.
    pdf.setDrawColor(180);
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(x, y, maxWidth, totalHeight, 2, 2, "FD");
  
    layoutSections.forEach((section) => {
      if (section.titleLines.length) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(titleFontSize);
        pdf.setTextColor(0, 0, 0);
        pdf.text(section.titleLines, x + titleLeftPad, y + section.titleY);
  
        section.extraLineItems.forEach((line) => {
          if (line.type === "caption") {
            pdf.setFont("helvetica", "italic");
            pdf.setFontSize(6.5);
            pdf.setTextColor(80, 80, 80);
            pdf.text(line.text, x + extraLeftPad, y + line.y);
          } else {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(7.5);
            pdf.setTextColor(40, 40, 40);
            pdf.text(line.text, x + extraLeftPad, y + line.y);
          }
        });
  
        pdf.setDrawColor(220);
        pdf.line(x + 2, y + section.lineY, x + maxWidth - 2, y + section.lineY);
      }
  
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(labelFontSize);
      pdf.setTextColor(40, 40, 40);
  
      section.rows.forEach((row) => {
        const safeColor = /^#[0-9a-fA-F]{6}$/.test(row.color) ? row.color : "#cccccc";
        const hex = safeColor.replace("#", "");
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
  
        pdf.setFillColor(r, g, b);
        pdf.setDrawColor(120);
        pdf.rect(x + 2.5, y + row.squareY, boxSize, boxSize, "FD");
  
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(labelFontSize);
        pdf.setTextColor(40, 40, 40);
        pdf.text(row.labelLines, x + labelLeftPad, y + row.rowTopY + 2.2);
      });
    });
  }

function MapExportPanel({ map }) {
  const leafletMap = getLeaflet(map);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [paperSize, setPaperSize] = useState("A4");
  const [orientation, setOrientation] = useState("landscape");
  const [title, setTitle] = useState("GIStatistica Croatica map");
  const [fileName, setFileName] = useState("gista-map");

  async function exportPdf() {
    await new Promise((r) => setTimeout(r, 300));
    if (!leafletMap) return;

    setLoading(true);

    try {
      const mapContainer = leafletMap.getContainer();
      if (!mapContainer) {
        throw new Error("A térkép konténere nem érhető el.");
      }

      const pdf = new jsPDF({
        orientation,
        unit: "mm",
        format: paperSize,
      });

      const [pageW, pageH] = getPdfSizeMM(paperSize, orientation);

      const margin = 10;
      const hasPdfTitle = Boolean(title && title.trim());
      const titleBlockH = hasPdfTitle ? 16 : 0;

      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2 - titleBlockH;

      const mapCanvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
      });

      const mapImg = mapCanvas.toDataURL("image/png");

      const imgWpx = mapCanvas.width;
      const imgHpx = mapCanvas.height;
      const imgRatio = imgWpx / imgHpx;

      let renderW = usableW;
      let renderH = usableW / imgRatio;

      if (renderH > usableH) {
        renderH = usableH;
        renderW = usableH * imgRatio;
      }

      if (hasPdfTitle) {
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        pdf.text(String(title).trim(), margin, margin + 7);
      }

      const imgX = margin + (usableW - renderW) / 2;
      const imgY = margin + titleBlockH;

      pdf.addImage(mapImg, "PNG", imgX, imgY, renderW, renderH);

      drawLegendSectionsToPdf(pdf, {
        map,
        mapX: imgX,
        mapY: imgY,
        mapW: renderW,
        mapH: renderH,
        inset: 4,
        maxWidth: 42,
        boxSize: 3,
        sectionGap: 3,
        titleFontSize: 8,
        labelFontSize: 7,
        corner: "bottom-right",
      });

      pdf.save(`${(fileName || "gista-map").trim()}.pdf`);
    } catch (e) {
      console.error("PDF export error:", e);
      alert(`PDF export error: ${e?.message || "unknown error"}`);
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <div style={{ position: "relative", zIndex: 1200, display: "inline-block" }}>
        <Tooltip title="Export map to PDF">
          <IconButton
            style={{ color: "gray" }}
            onClick={() => setOpen(true)}
            aria-label="open-map-export"
          >
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", zIndex: 1200, display: "inline-block" }}>
      <Paper
        elevation={10}
        style={{
          padding: 10,
          paddingLeft: 16,
          paddingRight: 16,
          width: "min(420px, calc(100vw - 30px))",
          borderRadius: 10,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f0f0f0",
        }}
      >
        <div style={{ background: "#f0f0f0" }}>
          <IconButton
            style={{ color: "gray", float: "right", marginRight: -6, marginTop: -6 }}
            onClick={() => setOpen(false)}
            aria-label="close"
          >
            <CloseIcon style={{ width: 20 }} />
          </IconButton>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "6px 8px",
              borderBottom: "1px solid #eee",
            }}
          >
            <h2>Map export</h2>
          </div>
        </div>

        <div style={{ padding: 12 }}>
          <Stack spacing={1.5}>
            <TextField
              label="PDF title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              fullWidth
            />

            <TextField
              label="File name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              size="small"
              fullWidth
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Paper size</InputLabel>
              <Select
                label="Paper size"
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value)}
              >
                <MenuItem value="A4">A4</MenuItem>
                <MenuItem value="A3">A3</MenuItem>
                <MenuItem value="A2">A2</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Orientation</InputLabel>
              <Select
                label="Orientation"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
              >
                <MenuItem value="landscape">Landscape</MenuItem>
                <MenuItem value="portrait">Portrait</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" style={{ color: "#555" }}>
              The export saves the current map extent to PDF, with separate legend sections.
            </Typography>

            {loading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Creating PDF…</Typography>
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={exportPdf}
                disabled={loading || !leafletMap}
                fullWidth
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
                disabled={loading}
                fullWidth
              >
                Close
              </Button>
            </div>
          </Stack>
        </div>
      </Paper>
    </div>
  );
}

export default connect(mapStateToProps)(MapExportPanel);